import {
  BorrowerDocumentationRequired,
  BorrowerFileType,
  BorrowerOnboardingDetails,
  CashInCashOutMethod,
  File,
  getCashInCashOutMethodById,
  getFilesFromBorrowerIdAndFileType,
  getIncomeById,
  getKycByExternalId,
  Income,
  Kyc,
  Loan,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import {
  BANK_ACCOUNT_DOCUMENT_FILE_TYPES,
  BIOMETRICS_FILE_TYPES,
  DOCUMENT_ID_FACE_FILE_TYPES,
  PASSPORT_DOCUMENT_ID_FILE_TYPES,
} from '@/components/modules/DocumentsValidation/constants';
import { DocumentationRequiredMap } from '@/components/modules/DocumentsValidation/maps/documentationRequired.map';
import { ValidationStep } from '@/components/modules/DocumentsValidation/types/ValidationStep';
import { useErrorBoundary } from '@/components/shared/components/ErrorBoundary/hooks/useErrorBoundary';

const initialState = {
  documentIdFaceFile: undefined as File | undefined | null,
  biometricsFaceFile: undefined as File | undefined | null,
  passportFile: undefined as File | undefined | null,
  securityChecks: undefined as Kyc['securityChecks'] | undefined,
  cashInCashOutMethod: undefined as CashInCashOutMethod | undefined,
  bankAccountFile: undefined as File | undefined | null,
  income: undefined as Income | undefined,
  incomeFiles: undefined as File[] | undefined | null,
  loading: false as boolean,
};

export const useFetchValidationData = () => {
  const [state, setState] = useState(initialState);
  const { throwErrorInAlert } = useErrorBoundary();

  const fetchVideoIdValidationData = async (
    loan: Loan,
    onboardingDetails: BorrowerOnboardingDetails,
  ): Promise<void> => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const documentIdFaceFile = await getDocumentFileFromTypeArray(loan, DOCUMENT_ID_FACE_FILE_TYPES);
      const biometricsFaceFile = await getDocumentFileFromTypeArray(loan, BIOMETRICS_FILE_TYPES);
      const securityChecks = await getSecurityChecks(loan, onboardingDetails);
      const passportFile = await getDocumentFileFromTypeArray(loan, PASSPORT_DOCUMENT_ID_FILE_TYPES);

      setState((state) => ({
        ...state,
        documentIdFaceFile: documentIdFaceFile || null,
        biometricsFaceFile: biometricsFaceFile || null,
        passportFile: passportFile || null,
        securityChecks,
        loading: false,
      }));
    } catch (error) {
      setState((state) => ({
        ...state,
        documentIdFaceFile: null,
        biometricsFaceFile: null,
        passportFile: null,
        loading: false,
      }));

      console.error(error);
      throwErrorInAlert('VIDEO_ID_VALIDATION_ERROR', () => fetchVideoIdValidationData(loan, onboardingDetails));
    }
  };

  const fetchBankAccountValidationData = async (loan: Loan): Promise<void> => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    const cashInCashOutMethod = await getCashInCashOutMethod(loan);
    const bankAccountFile = await getDocumentFileFromTypeArray(loan, BANK_ACCOUNT_DOCUMENT_FILE_TYPES);

    setState((state) => ({
      ...state,
      cashInCashOutMethod,
      bankAccountFile: bankAccountFile || null,
      loading: false,
    }));
  };

  const fetchIncomeValidationData = async (
    loan: Loan,
    onboardingDetails: BorrowerOnboardingDetails,
    documentation: BorrowerDocumentationRequired,
  ): Promise<void> => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    const incomeDocumentFileTypes = DocumentationRequiredMap.getFilesByStep(
      documentation,
      ValidationStep.INCOME_VALIDATION,
    );

    if (!incomeDocumentFileTypes) {
      throw new Error(`Not able to find incomeDocumentFileTypes for loan: ${loan.id}`);
    }

    const income = await getIncome(loan, onboardingDetails);

    if (incomeDocumentFileTypes.length === 0) {
      setState((state) => ({
        ...state,
        income,
        incomeFiles: null,
        loading: false,
      }));

      throw new Error(`Not able to find incomeDocumentFileTypes for loan: ${loan.id}`);
    }

    const documentFiles = await Promise.all(
      incomeDocumentFileTypes.map(
        async (incomeDocumentFileType) =>
          await getFilesFromBorrowerIdAndFileType({
            loanId: loan.id,
            borrowerId: loan.borrowerId as string,
            documentFileType: incomeDocumentFileType.name as unknown as BorrowerFileType,
          }).then(
            (files) => files.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0],
          ),
      ),
    );

    setState((state) => ({
      ...state,
      income,
      incomeFiles: documentFiles && documentFiles.length > 0 ? documentFiles : null,
      loading: false,
    }));
  };

  return {
    ...state,
    fetchVideoIdValidationData,
    fetchBankAccountValidationData,
    fetchIncomeValidationData,
  };
};

const getDocumentFileFromTypeArray = async (
  loan: Loan,
  fileTypes: readonly BorrowerFileType[],
): Promise<File | undefined> => {
  if (!loan.borrowerId) {
    throw new Error(`Not able to find borrowerId of loan: ${loan.id}`);
  }

  try {
    for (const fileType of fileTypes) {
      const documentFiles = await getFilesFromBorrowerIdAndFileType({
        loanId: loan.id,
        borrowerId: loan.borrowerId,
        documentFileType: fileType,
      });

      if (documentFiles.length > 0) {
        return documentFiles.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      }
    }
  } catch (error) {
    throw new Error(`Not able to fetch document file for loan: ${loan.id}`);
  }
};

const getSecurityChecks = async (
  loan: Loan,
  onboardingDetails: BorrowerOnboardingDetails,
): Promise<Kyc['securityChecks']> => {
  try {
    const kycArray = await getKycByExternalId({ loanId: loan.id });
    const documentNumber = onboardingDetails.userDetails?.documentId.documentNumber;

    if (!documentNumber) {
      throw new Error(`Not able to find documentNumber in onboardingDetails id : ${onboardingDetails.id}`);
    }

    const borrowerKyc = kycArray.find((kyc) => kyc.documentNumber === documentNumber);

    if (!borrowerKyc) {
      throw new Error(`Not able to find borrowerKyc for documentNumber: ${documentNumber}`);
    }

    return borrowerKyc.securityChecks;
  } catch (error) {
    throw new Error(`Not able to fetch securityChecks for onboardingDetailsId: ${onboardingDetails.id}`);
  }
};

const getCashInCashOutMethod = async (loan: Loan): Promise<CashInCashOutMethod> => {
  if (!loan.borrowerCashInCashOutMethodId) {
    throw new Error(`Not able to find borrowerCashInCashOutMethodId for loan: ${loan.id}`);
  }

  try {
    return await getCashInCashOutMethodById({
      id: loan.borrowerCashInCashOutMethodId,
    });
  } catch (error) {
    throw new Error(`Not able to fetch borrower cashInCashOutMethod for loan: ${loan.id}`);
  }
};

const getIncome = async (loan: Loan, onboardingDetails: BorrowerOnboardingDetails): Promise<Income> => {
  try {
    return getIncomeById({ loanId: loan.id, onboardingDetailsId: onboardingDetails.id });
  } catch (error) {
    throw new Error(`Not able to fetch income for onboardingDetailsId: ${onboardingDetails.id}`);
  }
};
